import type { CSSProperties, ReactNode } from 'react';
import { useMemo } from 'react';
import { ContainerPreset } from '@/constants/componentPresets';
import { ColorType } from '@/theme/colors';
import {
  buildContainerStyle,
  buildContentWrapperStyle,
  buildGradientBorderStyle,
  buildLoadingBorderStyle,
  resolveContainerStyleValues,
} from './CcontainerStyles';
import { CONTAINER_PRESET_CONFIGS } from './CcontainerPresets';
import type { CcontainerProps } from './Ccontainer.types';
import type { ContainerLoadingBorderState } from './useContainerLoadingBorder';

const DEFAULT_BACKDROP_BLUR = 0;
const DEFAULT_GLOW = 0;
const DEFAULT_GLOSSY = 0;
const DEFAULT_ANIMATION_DURATION_MS = 200;
const DEFAULT_DISPLAY: NonNullable<CcontainerProps['display']> = 'flex';
const DEFAULT_INNER_BORDER = true;
const DEFAULT_SMOOTH = true;
const DEFAULT_CLICKABLE = true;
const DEFAULT_DISABLE_HOVER_SHADOW = false;

type ContainerStyleValues = ReturnType<typeof resolveContainerStyleValues>;
type ContainerLoadingStyleState = Pick<
  ContainerLoadingBorderState,
  'isLoadingActive' | 'loadingOpacity' | 'gradientAngle' | 'borderRevealProgress' | 'endStopOpacity'
>;

interface UseContainerStyleModelInput {
  props: CcontainerProps;
  isHovered: boolean;
  loadingBorder: ContainerLoadingStyleState;
}

interface UseContainerStyleValuesInput {
  props: CcontainerProps;
  isHovered: boolean;
}

interface UseContainerRootStyleInput {
  props: CcontainerProps;
  styleValues: ContainerStyleValues;
  useGradientBorder: boolean;
  useGradientBackground: boolean;
  hasGlowLayer: boolean;
  isLoadingActive: boolean;
}

interface UseContainerGradientBorderStyleInput {
  styleValues: ContainerStyleValues;
  useGradientBorder: boolean;
}

interface UseContainerContentWrapperStyleInput {
  props: CcontainerProps;
  finalFlexDirection?: CcontainerProps['flexDirection'];
  loadingBorder: ContainerLoadingStyleState;
}

export interface ContainerStyleModel {
  containerStyle: CSSProperties;
  contentWrapperStyle: CSSProperties;
  gradientBorderStyle?: CSSProperties;
  loadingBorderStyle?: CSSProperties;
  hasGlowLayer: boolean;
  useGradientBorder: boolean;
  isLoadingActive: boolean;
}

export function useContainerStyleModel(input: UseContainerStyleModelInput): ContainerStyleModel {
  const { props, isHovered, loadingBorder } = input;
  const styleValues = useContainerStyleValues({ props, isHovered });
  const useGradientBorder = styleValues.finalBorderGradient != null;
  const useGradientBackground = styleValues.finalBackgroundGradient != null;
  const hasGlowLayer = hasContainerGlowLayer(props.glowLayer);
  const containerStyle = useContainerRootStyle({
    props,
    styleValues,
    useGradientBorder,
    useGradientBackground,
    hasGlowLayer,
    isLoadingActive: loadingBorder.isLoadingActive,
  });
  const gradientBorderStyle = useContainerGradientBorderStyle({ styleValues, useGradientBorder });
  const loadingBorderStyle = useContainerLoadingLayerStyle({ loadingBorder });
  const contentWrapperStyle = useContainerContentWrapperStyle({
    props,
    finalFlexDirection: styleValues.finalFlexDirection,
    loadingBorder,
  });

  return {
    containerStyle,
    contentWrapperStyle,
    gradientBorderStyle,
    loadingBorderStyle,
    hasGlowLayer,
    useGradientBorder,
    isLoadingActive: loadingBorder.isLoadingActive,
  };
}

function useContainerStyleValues(input: UseContainerStyleValuesInput): ContainerStyleValues {
  const { props, isHovered } = input;
  const {
    radius,
    borderColor,
    borderGradient,
    borderOpacity,
    borderWidth,
    backgroundColor,
    backgroundGradient,
    backgroundOpacity,
    backdropBlur = DEFAULT_BACKDROP_BLUR,
    padding,
    width,
    height,
    glow = DEFAULT_GLOW,
    boxShadow,
    disableHoverShadow = DEFAULT_DISABLE_HOVER_SHADOW,
    flexDirection,
    overflow,
    preset = ContainerPreset.Default,
  } = props;
  const presetConfig = CONTAINER_PRESET_CONFIGS[preset];

  return useMemo(() => resolveContainerStyleValues({
    presetConfig,
    radius,
    borderColor,
    borderGradient,
    borderOpacity,
    borderWidth,
    backgroundColor,
    backgroundGradient,
    backgroundOpacity,
    backdropBlur,
    padding,
    width,
    height,
    glow,
    boxShadow,
    disableHoverShadow,
    flexDirection,
    overflow,
    isHovered,
  }), [
    presetConfig, radius, borderColor, borderGradient, borderOpacity, borderWidth,
    backgroundColor, backgroundGradient, backgroundOpacity, backdropBlur, padding, width,
    height, glow, boxShadow, disableHoverShadow, flexDirection, overflow, isHovered,
  ]);
}

function useContainerRootStyle(input: UseContainerRootStyleInput): CSSProperties {
  const {
    props,
    styleValues,
    useGradientBorder,
    useGradientBackground,
    hasGlowLayer,
    isLoadingActive,
  } = input;
  const {
    display = DEFAULT_DISPLAY,
    alignItems,
    justifyContent,
    gap,
    glowColor = ColorType.Accent,
    glossy = DEFAULT_GLOSSY,
    innerBorder = DEFAULT_INNER_BORDER,
    smooth = DEFAULT_SMOOTH,
    animationDuration = DEFAULT_ANIMATION_DURATION_MS,
    onClick,
    clickable = DEFAULT_CLICKABLE,
    style,
  } = props;

  return useMemo(() => buildContainerStyle({
    values: styleValues,
    display,
    alignItems,
    justifyContent,
    gap,
    glowColor,
    glossy,
    innerBorder,
    useGradientBorder,
    useGradientBackground,
    hasGlowLayer,
    isLoadingActive,
    smooth,
    animationDuration,
    onClick,
    clickable,
    style,
  }), [
    styleValues, display, alignItems, justifyContent, gap, glowColor, glossy,
    innerBorder, useGradientBorder, useGradientBackground, hasGlowLayer, isLoadingActive,
    smooth, animationDuration, onClick, clickable, style,
  ]);
}

function useContainerGradientBorderStyle(input: UseContainerGradientBorderStyleInput): CSSProperties | undefined {
  const { styleValues, useGradientBorder } = input;
  const { finalBorderGradient, finalBorderWidth } = styleValues;

  return useMemo(() => buildGradientBorderStyle({
    useGradientBorder,
    finalBorderGradient,
    finalBorderWidth,
  }), [finalBorderGradient, finalBorderWidth, useGradientBorder]);
}

function useContainerLoadingLayerStyle(input: {
  loadingBorder: ContainerLoadingStyleState;
}): CSSProperties | undefined {
  const { isLoadingActive, gradientAngle, borderRevealProgress, endStopOpacity } = input.loadingBorder;

  return useMemo(() => buildLoadingBorderStyle({
    isLoadingActive,
    gradientAngle,
    borderRevealProgress,
    endStopOpacity,
  }), [borderRevealProgress, endStopOpacity, gradientAngle, isLoadingActive]);
}

function useContainerContentWrapperStyle(input: UseContainerContentWrapperStyleInput): CSSProperties {
  const { props, finalFlexDirection, loadingBorder } = input;
  const {
    display = DEFAULT_DISPLAY,
    alignItems,
    justifyContent,
    gap,
    contentWrapperStyle: externalContentWrapperStyle,
  } = props;
  const { isLoadingActive, loadingOpacity } = loadingBorder;

  return useMemo(() => buildContentWrapperStyle({
    display,
    finalFlexDirection,
    alignItems,
    justifyContent,
    gap,
    externalContentWrapperStyle,
    isLoadingActive,
    loadingOpacity,
  }), [
    display, finalFlexDirection, alignItems, justifyContent, gap,
    externalContentWrapperStyle, isLoadingActive, loadingOpacity,
  ]);
}

function hasContainerGlowLayer(glowLayer: ReactNode): boolean {
  return glowLayer != null && glowLayer !== false;
}
